// ConfirmationDialog.jsx - Simple yes/no confirmation dialog
// For Cancel, Delete, and other simple confirmations
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from "components/mui";

/**
 * Reusable confirmation dialog for simple yes/no confirmations
 *
 * Follows the same MUI patterns as ConfirmationModal.jsx
 *
 * Use Cases:
 * - Cancel editing confirmation
 * - Delete scheduled email confirmation
 * - Warning prompts
 *
 * DO NOT use for:
 * - Email sending/scheduling (use ConfirmationModal.jsx instead)
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Called when dialog closes (Cancel or backdrop)
 * @param {function} props.onConfirm - Called when user confirms action
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Main message text
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.severity - Alert severity: "warning", "error", "info" (default: "warning")
 * @param {string} props.confirmButtonColor - MUI color for confirm button (default: "primary")
 * @param {string} props.confirmButtonVariant - MUI variant for confirm button (default: "contained")
 */
function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  severity = "warning",
  confirmButtonColor = "primary",
  confirmButtonVariant = "contained",
}) {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        sx={{
          minWidth: 400,
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Alert severity={severity} sx={{ mb: 1 }}>
          <Typography variant="body2">{message}</Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button color="error" onClick={onClose}>
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant={confirmButtonVariant}
          color={confirmButtonColor}
          sx={{ minWidth: 90, fontWeight: 600 }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;
